/** @jsxImportSource @emotion/react */
import FormSingleSelectField from 'components/FormBuilder/FormSingleSelectField';
import { SelectOption } from 'models/formSingleSelectField';
import { Link, navigate } from '@reach/router';
import instance, { ApiResponse } from 'api';
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { FormDropdownField } from 'components/pagination/Dropdown';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers } from 'formik';
import { CurrencyDetails } from 'pages/register/models';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCustomerComponentResources,
    selectAccountTypePathItem,
} from 'reducers/componentResources';
import { selectCultureCode } from 'reducers/language';

import { BrandVisual } from 'components/brandVisual/BrandVisual';
import { useBrandIdentity } from 'hooks/useBrandIdentity';
import { css, useTheme } from '@emotion/react';
import * as Yup from 'yup';
import { Toast } from 'helpers/toast';

const initialValues = {
    currencyCode: 'USD',
    accountName: '',
};

const validationSchema = Yup.object({
    accountName: Yup.string().required('Please choose an account name'),
});

export const AddAccount = () => {
    const [availableCurrencyCodes, setAvailableCurrencyCodes] = useState<SelectOption[]>([]);
    const theme = useTheme();
    const { accountTypeImage, companyName } = useBrandIdentity();

    const addAccountCss = css({ h3: { color: theme.colors.second } });

    const cultureCode = useSelector(selectCultureCode);
    const accountTypePathItem = useSelector(selectAccountTypePathItem);
    const dispatch = useDispatch();

    const { t } = useTranslation('common');

    useEffect(() => {
        getAvailableCurrencies();
    }, []);

    const getAvailableCurrencies = () => {
        instance
            .get<ApiResponse<CurrencyDetails[]>>(endpoints.registration.availableCurrencies)
            .then((res) => {
                setAvailableCurrencyCodes(
                    res.data.details.map((curr) => ({
                        value: curr.code,
                        label: `${curr.name} - ${curr.symbol}`,
                    }))
                );
            })
            .catch((err) => {
                Toast.openGenericErrorToast();
                setAvailableCurrencyCodes([]); //TODO handle error
            });
    };

    const handleSubmit = (
        values: typeof initialValues,
        formikHelpers: FormikHelpers<typeof initialValues>
    ) => {
        instance
            .post<ApiResponse<{ accountUrlPath: string }>>(endpoints.accounts.addAccount, values)
            .then((res) => {
                dispatch(getCustomerComponentResources());
                return navigate(`../${res.data.details.accountUrlPath}`);
            })
            .catch((err) => {
                formikHelpers.setSubmitting(false);
                Toast.openGenericErrorToast();
            });
    };
    return (
        <div className="AddAccountPage" css={addAccountCss}>
            <div className="LeftSide">
                <h3>{'Add New Account'}</h3>
                <p>{'Opening another account with us will allow you to control your personal finances separately.'}</p>
                <p>{'Enter the correct details below to get started.'}</p>
                <h3>{'Account Details'}</h3>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            {/* <FormSingleSelectField
                                fieldName="currencyCode"
                                label={translations.currency}
                                options={availableCurrencyCodes}
                            /> */}
                            <FormTextField
                                field="accountName"
                                label={'Account Name'}
                                required
                                placeholder={'E.g. Crypto Trading'}
                            />
                            <Button variety="full" type="submit" disabled={isSubmitting}>
                                {'Create Account'}
                            </Button>
                        </Form>
                    )}
                </Formik>
                <div className="LegalText">
                    {'By clicking the "Create Account" button above, you agree to our '}
                    <Link to={`/${cultureCode}/${accountTypePathItem}/info/terms-conditions`}>
                        {'terms and conditions.'}
                    </Link>
                    {` To learn more about how ${companyName} collects, uses, shares and protects your personal data please read our `}
                    <Link to={`/${cultureCode}/${accountTypePathItem}/info/security-privacy`}>
                        {'security and privacy policy.'}
                    </Link>
                </div>
            </div>
            <BrandVisual src={accountTypeImage} />
        </div>
    );
};
